/* @flow */
import { get, isNumber } from 'lodash';
import pluralize from 'pluralize';
import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AdminStatisticsChartTooltip from 'admin/components/AdminStatisticsChartTooltip';
import AdminStatisticsChartTooltipValue from 'admin/components/AdminStatisticsChartTooltipValue';
import AdminStatisticsChartTooltipValuePercent from 'admin/components/AdminStatisticsChartTooltipValuePercent';
import AdminStatisticsChartXAxisTick from 'admin/components/AdminStatisticsChartXAxisTick';
import {
  MASCOT,
  CHART_ACTIVE_DOT_RADIUS,
  CHART_AREA_FILL_OPACITY,
  CHART_AXIS_FILL,
  CHART_GRID_COLOR,
  CHART_HEIGHT,
  CHART_HEIGHT_SIMPLE,
  CHART_LEGEND_ALIGN,
  CHART_LEGEND_ICON_SIZE,
  CHART_MARGIN,
  CHART_MARKER_TYPE,
  CHART_MARKER_LEGEND_TYPE,
  BALLPOINT,
} from 'admin/constants/Charts';
import Row from 'components/Row';

type Props = {
  announcementsColor?: string,
  conversationsColor?: string,
  data: Array<Object>,
  height?: number,
  legend?: boolean,
  simple?: boolean,
  tooltip?: boolean,
};

function AdminStatisticsCommunicationChartTooltip(
  // $FlowIssue
  props: { active: boolean, label: string, payload: Array }
) {
  const { active } = props;

  if (!active) {
    return null;
  }

  const { label, payload } = props;

  // when not showing axis, the `title` is just an index & not the date
  const title = isNumber(label) ? get(payload, '[0].payload.Date') : label;

  const messagesTotal = payload[0].value + payload[1].value;

  return (
    <AdminStatisticsChartTooltip title={title || label}>
      <Row size="small">
        <AdminStatisticsChartTooltipValue element="div">
          {messagesTotal}
        </AdminStatisticsChartTooltipValue>
        {pluralize('Messages', messagesTotal)}, delivered total
      </Row>
      <Row size="small">
        <div>
          <AdminStatisticsChartTooltipValue>{payload[0].value}</AdminStatisticsChartTooltipValue>{' '}
          <AdminStatisticsChartTooltipValuePercent value={payload[0].value} total={messagesTotal} />
        </div>
        {pluralize(payload[0].name, payload[0].value)}
      </Row>
      <div>
        <AdminStatisticsChartTooltipValue>{payload[1].value}</AdminStatisticsChartTooltipValue>{' '}
        <AdminStatisticsChartTooltipValuePercent value={payload[1].value} total={messagesTotal} />
      </div>
      {pluralize(payload[1].name, payload[1].value)}
    </AdminStatisticsChartTooltip>
  );
}

export default function AdminStatisticsCommunicationChart({
  announcementsColor,
  conversationsColor,
  data,
  height,
  legend,
  simple,
  tooltip,
}: Props) {
  const chartHeight = simple ? CHART_HEIGHT_SIMPLE : CHART_HEIGHT;

  return (
    <div className="AdminStatisticsCommunicationChart">
      <ResponsiveContainer width="100%" height={height || chartHeight}>
        <AreaChart data={data} margin={CHART_MARGIN}>
          {!simple && (
            <XAxis
              axisLine={false}
              dataKey="Date"
              // $FlowIssue
              tick={<AdminStatisticsChartXAxisTick />}
              tickLine={false}
            />
          )}
          {!simple && (
            <YAxis
              allowDecimals={false}
              axisLine={false}
              tick={{ fill: CHART_AXIS_FILL }}
              tickLine={false}
              tickFormatter={value => value.toLocaleString()}
            />
          )}
          {!simple && <CartesianGrid stroke={CHART_GRID_COLOR} vertical={false} />}
          {(legend || !simple) && (
            <Legend align={CHART_LEGEND_ALIGN} iconSize={CHART_LEGEND_ICON_SIZE} />
          )}
          {(tooltip || !simple) && (
            <Tooltip
              content={<AdminStatisticsCommunicationChartTooltip />}
              isAnimationActive={false}
            />
          )}
          <Area
            activeDot={{ r: CHART_ACTIVE_DOT_RADIUS }}
            dataKey="Conversations"
            dot={false}
            fill={announcementsColor}
            fillOpacity={CHART_AREA_FILL_OPACITY}
            legendType={CHART_MARKER_LEGEND_TYPE}
            stackId="1"
            stroke={announcementsColor}
            type={CHART_MARKER_TYPE}
            isAnimationActive={false}
          />
          <Area
            activeDot={{ r: CHART_ACTIVE_DOT_RADIUS }}
            dataKey="Announcements"
            dot={false}
            fill={conversationsColor}
            fillOpacity={CHART_AREA_FILL_OPACITY}
            legendType={CHART_MARKER_LEGEND_TYPE}
            stackId="1"
            stroke={conversationsColor}
            type={CHART_MARKER_TYPE}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

AdminStatisticsCommunicationChart.defaultProps = {
  announcementsColor: BALLPOINT,
  conversationsColor: MASCOT,
  legend: true,
  tooltip: true,
};
